

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { resolveSignageSessionOrganizationId } from "../../api/salesOrderApi";
import { createPortal } from "react-dom";
import { useLoadScript } from "@react-google-maps/api";
import Select, { components, type GroupBase, type StylesConfig } from "react-select";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { showError, showSuccess } from "toast/toast";
import { sendTriggerEmails } from "utils/emailService";
import { resolveBrokerageFirmEmail } from "utils/brokerageTeamMemberEmail";
import { addressLine1Validation } from "validationSchemas/common";
import AddressAutocomplete from "components/AddressAutocomplete";
import SearchBrokerageAgentTeam, {
  type SearchBrokerageAgentTeamHandle,
} from "components/SearchBrokerageAgentTeam";
import BrokerageTeammemberCreationComponent, {
  type BrokerageTeammemberCreatedResult,
} from "components/BrokerageTeammemberCreationComponent";
import "components/SearchBrokerageAgentTeam.css";
import {
  Office,
  createSignageProject,
  fetchProjectById,
  fetchProjectPublicAccess,
  parseCreatedProjectId,
  postProjectMessage,
  uploadSignageProjectFiles,
  fetchOfficesByFirm,
  fetchUsersSearchSignageTeamMembers,
  resolveSignageProjectAddressForCreate,
  resolveSignageProjectAddressForUpdate,
  buildProjectMembershipIdentityPayload,
  SignageDesignerSelectOption,
  renderSignagePersonOptionLabel,
  SignageProjectDetail,
  updateProjectLinkedOrderType,
} from "components/proejctsandquoteapicalls";
import "./projectandquote-addedit.css";
import { getAuthCookie } from "../../utils/authCookies";

const apiKey: string = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

type ProjectSearchContact = {
  customer_id?: string;
  fName: string;
  lName: string;
  email: string;
  phone: string;
};

const emptyProjectContact: ProjectSearchContact = {
  customer_id: "",
  fName: "",
  lName: "",
  email: "",
  phone: "",
};

/** API may return `customer_phone` and/or `address.phone` / `address.phone1`. */
function getCustomerPhoneFromProjectDetail(
  detail: SignageProjectDetail | null | undefined
): string {
  if (!detail) return "";
  const top = String(
    detail.customer_phone ?? (detail as { phone1?: string }).phone1 ?? ""
  ).trim();
  if (top) return top;
  const addr = detail.address as Record<string, unknown> | undefined;
  if (addr && typeof addr === "object") {
    const nested = String(
      addr.phone ?? (addr as { phone1?: string }).phone1 ?? ""
    ).trim();
    if (nested) return nested;
  }
  return "";
}

/** Prefer contact phone; if empty, use resolved address phone (so customer_phone is not dropped). */
function pickCustomerPhone(contactPhone: string, addressPhone: string): string {
  const c = (contactPhone || "").trim();
  if (c) return c;
  return (addressPhone || "").trim();
}

/** Team member + brokerage + office + customer. */
const TEAM_MEMBER_PROJECT_LICENSES = new Set([
  "SIGNAGE_TEAM_MEMBER",
  "BROKERAGE_TEAM_MEMBER",
  "TEAM_MEMBER",
]);

type OfficePayload = { _id: string; officeName: string };

const EMPTY_PROJECT_OFFICE: OfficePayload = { _id: "", officeName: "" };

/** Contact + display names for POST/PATCH /projects (IDs go via membership identity fields). */
type ProjectPartyPayload = {
  agent_name?: string;
  team_member_name?: string;
  brokerage_firm_name?: string;
  office?: OfficePayload;
  customer_email: string;
  customer_name: string;
  customer_phone: string;
  first_name: string;
  last_name: string;
  license_type: string;
};

/** Membership id used as customer_membership_id (legacy customer_id). */
function resolveProjectCustomerMembershipId(args: {
  licenseType: string;
  agentId: string;
  teamMemberId?: string;
  customer_id?: string;
  contactCustomerId?: string;
}): string {
  const lt = String(args.licenseType || "").toUpperCase();
  const teamMemberId = String(args.teamMemberId ?? "").trim();
  const agentId = String(args.agentId ?? "").trim();
  const baseCustomerId = String(
    args.customer_id || args.contactCustomerId || agentId || teamMemberId || ""
  ).trim();
  if (lt === "BROKERAGE_FIRM") {
    if (agentId) return agentId;
    if (teamMemberId) return teamMemberId;
    return baseCustomerId;
  }
  if (TEAM_MEMBER_PROJECT_LICENSES.has(lt) || (lt === "OFFICE" && (agentId || teamMemberId))) {
    return agentId || teamMemberId;
  }
  if (AGENT_LIKE_LICENSES.has(lt) || lt === "AGENT" || lt === "BROKERAGE_AGENT") {
    return agentId || teamMemberId;
  }
  return baseCustomerId;
}

/** PATCH merges omitted keys — on edit person change, send "" to clear stale party fields. */
function finalizePartyPayloadForPatch(core: ProjectPartyPayload): ProjectPartyPayload {
  return {
    ...core,
    agent_name: String(core.agent_name ?? "").trim(),
    team_member_name: String(core.team_member_name ?? "").trim(),
    brokerage_firm_name: String(core.brokerage_firm_name ?? "").trim(),
    office: core.office?._id
      ? {
          _id: String(core.office._id).trim(),
          officeName: String(core.office.officeName ?? "").trim(),
        }
      : EMPTY_PROJECT_OFFICE,
  };
}

function buildProjectPartyPayload(
  args: {
    licenseType: string;
    agentId: string;
    agentName: string;
    teamMemberId?: string;
    teamMemberName?: string;
    customer_id?: string;
    brokerage_firm_id: string;
    brokerage_firm_name: string;
    office: OfficePayload;
    contact: ProjectSearchContact;
    customer_phone: string;
  },
  options?: { forPatchPersonChange?: boolean }
): ProjectPartyPayload {
  const lt = String(args.licenseType || "").toUpperCase();
  const teamMemberId = String(args.teamMemberId ?? "").trim();
  const teamMemberName = String(args.teamMemberName ?? "").trim();
  const agentId = String(args.agentId ?? "").trim();
  const agentName = String(args.agentName ?? "").trim();
  const customer_email = String(args.contact.email || "").trim();
  const first_name = String(args.contact.fName || "").trim();
  const last_name = String(args.contact.lName || "").trim();
  const customer_name =
    [args.contact.fName, args.contact.lName].filter(Boolean).join(" ").trim() ||
    agentName ||
    teamMemberName ||
    "";

  const baseCustomer = {
    customer_email,
    customer_name,
    customer_phone: args.customer_phone,
    first_name,
    last_name,
  };

  const hasOfficeId = String(args.office?._id ?? "").trim() !== "";
  const officePart = hasOfficeId ? { office: args.office } : {};
  const brokerageFirmName = String(args.brokerage_firm_name ?? "").trim();
  const brokeragePart = brokerageFirmName
    ? { brokerage_firm_name: brokerageFirmName }
    : {};

  let core: ProjectPartyPayload;

  if (lt === "BROKERAGE_FIRM") {
    core = {
      ...brokeragePart,
      ...officePart,
      ...(teamMemberName ? { team_member_name: teamMemberName } : {}),
      ...(agentName ? { agent_name: agentName } : {}),
      ...baseCustomer,
      license_type: lt,
    };
  } else if (TEAM_MEMBER_PROJECT_LICENSES.has(lt) || (lt === "OFFICE" && (agentId || teamMemberId))) {
    core = {
      ...brokeragePart,
      ...officePart,
      ...(teamMemberName ? { team_member_name: teamMemberName } : {}),
      ...(agentName ? { agent_name: agentName } : {}),
      ...baseCustomer,
      license_type: lt,
    };
  } else if (lt === "OFFICE" && !agentId && !teamMemberId) {
    core = {
      ...brokeragePart,
      ...officePart,
      ...baseCustomer,
      license_type: lt,
    };
  } else {
    core = {
      agent_name: agentName || teamMemberName,
      ...baseCustomer,
      license_type: lt,
    };
  }

  return options?.forPatchPersonChange ? finalizePartyPayloadForPatch(core) : core;
}

type ProjectPersonPick = {
  id: string;
  membershipId?: string;
  globalUserId?: string;
  organizationId?: string;
  label: string;
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
};

function resolveBrokerageFirmOrganizationId(args: {
  licenseType: string;
  item: any;
  brokerage_firm_id: string;
}): string {
  const lt = String(args.licenseType || "").toUpperCase();
  const item = args.item || {};
  if (lt === "BROKERAGE_FIRM") {
    return String(
      item.organization_id ??
        item.organizationId ??
        item.brokerage_firm_organization_id ??
        item.brokerageFirmOrganizationId ??
        item.value ??
        args.brokerage_firm_id ??
        ""
    ).trim();
  }
  return String(
    item.brokerage_firm_organization_id ??
      item.brokerageFirmOrganizationId ??
      item.brokerageFirmOrgId ??
      item.brokerage_firm_org_id ??
      ""
  ).trim();
}

function buildProjectIdentityFields(args: {
  organizationId: string;
  membershipId: string;
  globalUserId: string;
  licenseType?: string;
  searchItem?: any;
  savedProject?: SignageProjectDetail | null;
  agentMembershipId?: string;
  agentGlobalUserId?: string;
  agentOrgId?: string;
  teamMemberMembershipId?: string;
  teamMemberGlobalUserId?: string;
  projectTeamMemberPick?: ProjectPersonPick | null;
  projectAgentPick?: ProjectPersonPick | null;
  customerMembershipId?: string;
  customerGlobalUserId?: string;
  brokerageFirmOrganizationId?: string;
  designer?: SignageDesignerSelectOption | null;
  assignedTo?: SignageDesignerSelectOption | null;
}) {
  return buildProjectMembershipIdentityPayload({
    session: {
      organizationId: args.organizationId,
      membershipId: args.membershipId,
      globalUserId: args.globalUserId,
    },
    licenseType: args.licenseType,
    searchItem: args.searchItem,
    savedProject: args.savedProject as Record<string, unknown> | null,
    agentMembershipId: args.agentMembershipId,
    agentGlobalUserId: args.agentGlobalUserId,
    agentOrgId: args.agentOrgId,
    teamMemberMembershipId: args.teamMemberMembershipId,
    teamMemberGlobalUserId: args.teamMemberGlobalUserId,
    projectTeamMemberPick: args.projectTeamMemberPick,
    projectAgentPick: args.projectAgentPick,
    customerMembershipId: args.customerMembershipId,
    customerGlobalUserId: args.customerGlobalUserId,
    brokerageFirmOrganizationId: args.brokerageFirmOrganizationId,
    designer: args.designer,
    assignedTo: args.assignedTo,
  });
}

function nameFromSearchItem(item: any): string {
  if (!item) return "";
  const fromParts = [item.firstName, item.lastName]
    .map((part) => String(part ?? "").trim())
    .filter(Boolean)
    .join(" ")
    .trim();
  return fromParts || String(item.displayLabel || item.label || "").trim();
}

function resolveProjectPersonPicks(args: {
  licenseType: string;
  item: any;
  contact: ProjectSearchContact;
  projectTeamMemberPick: ProjectPersonPick | null;
  projectAgentPick: ProjectPersonPick | null;
}) {
  const lt = String(args.licenseType || "").toUpperCase();
  const isDirectTeamMemberEntity =
    TEAM_MEMBER_PROJECT_LICENSES.has(lt) && !args.projectTeamMemberPick;

  const teamMemberId = String(
    args.projectTeamMemberPick?.membershipId ??
      args.projectTeamMemberPick?.id ??
      (isDirectTeamMemberEntity ? args.item?.membershipId ?? args.item?.value : "") ??
      ""
  ).trim();
  const teamMemberName = args.projectTeamMemberPick
    ? `${args.projectTeamMemberPick.firstName || ""} ${args.projectTeamMemberPick.lastName || ""}`.trim() ||
      args.projectTeamMemberPick.label ||
      ""
    : isDirectTeamMemberEntity
      ? nameFromSearchItem(args.item)
      : "";

  const agentId = args.projectAgentPick
    ? String(args.projectAgentPick.membershipId ?? args.projectAgentPick.id)
    : AGENT_LIKE_LICENSES.has(lt) && !TEAM_MEMBER_PROJECT_LICENSES.has(lt) && !args.projectTeamMemberPick
      ? String(args.item?.membershipId ?? args.item?.value ?? "")
      : "";

  const agentName = args.projectAgentPick
    ? `${args.projectAgentPick.firstName || ""} ${args.projectAgentPick.lastName || ""}`.trim() ||
      args.projectAgentPick.label ||
      ""
    : AGENT_LIKE_LICENSES.has(lt) &&
        !TEAM_MEMBER_PROJECT_LICENSES.has(lt) &&
        !args.projectTeamMemberPick
      ? [args.contact.fName, args.contact.lName].filter(Boolean).join(" ").trim() ||
        String(args.item?.label || "")
      : "";

  const primaryPersonId =
    agentId ||
    teamMemberId ||
    (AGENT_LIKE_LICENSES.has(lt) && !TEAM_MEMBER_PROJECT_LICENSES.has(lt)
      ? String(args.item?.value ?? "")
      : "");

  const customer_id = String(
    args.contact.customer_id || agentId || teamMemberId || ""
  ).trim();

  const customerGlobalUserId = String(
    args.projectAgentPick?.globalUserId ??
      args.projectTeamMemberPick?.globalUserId ??
      args.item?.global_user_id ??
      args.item?.globalUserId ??
      ""
  ).trim();

  return {
    teamMemberId,
    teamMemberName,
    agentId,
    agentName,
    primaryPersonId,
    customer_id,
    customerGlobalUserId,
  };
}

function requiresAgentOrTeamMemberId(licenseType: string): boolean {
  const lt = String(licenseType || "").toUpperCase();
  if (lt === "BROKERAGE_FIRM") return false;
  if (lt === "OFFICE") return false;
  return true;
}

const AGENT_LIKE_LICENSES = new Set([
  "AGENT",
  "BROKERAGE_AGENT",
  "BROKERAGE_TEAM_MEMBER",
  "TEAM_MEMBER",
]);

const ARTWORK_INPUT_ACCEPT = ".png,.jpg,.jpeg,.pdf,.ai,image/png,image/jpeg,application/pdf";

function isAllowedArtworkFile(file: File): boolean {
  const ext = file.name.split(".").pop()?.toLowerCase() || "";
  if (["png", "jpg", "jpeg", "pdf", "ai"].includes(ext)) return true;
  const t = file.type || "";
  return (
    t === "image/png" ||
    t === "image/jpeg" ||
    t === "application/pdf" ||
    t === "application/postscript"
  );
}

type WorkflowItem = {
  label: string;
  detail: string;
  status?: string;
  assignedTo?: string;
  iconClass: string;
  tone: "blue" | "purple" | "green" | "orange" | "mint";
};

const createWorkflowItems: WorkflowItem[] = [
  {
    label: "Project created",
    detail: "New project initiated with user details",
    iconClass: "ri-add-line",
    tone: "blue",
  },
  {
    label: "Design discussions",
    detail: "Designer and user collaborate on requirements",
    iconClass: "ri-message-2-line",
    tone: "purple",
  },
  {
    label: "Design approved",
    detail: "Final artwork is confirmed by user",
    assignedTo: "Assigned to: Bryan (Pricing)",
    iconClass: "ri-thumb-up-line",
    tone: "green",
  },
  {
    label: "Quote sent",
    detail: "Pricing finalized and shared for payment",
    iconClass: "ri-file-list-3-line",
    tone: "orange",
  },
  // {
  //   label: "Payment completed and Converted",
  //   detail: "Order ready for production",
  //   iconClass: "ri-shopping-cart-line",
  //   tone: "mint",
  // },
];

type EditWorkflowStatusUi = {
  badge: string;
  title: string;
  subtitle: string;
  icon: string;
  iconClass: string;
  badgeClass: string;
};

const getEditWorkflowStatusUi = (status: string | undefined): EditWorkflowStatusUi => {
  const s = (status || "").toLowerCase();
  if (s === "quote_approved") {
    return {
      badge: "Quote Approved",
      title: "Quote approved",
      subtitle: "Customer approved the quote",
      icon: "ri-shield-check-line",
      iconClass: "approved",
      badgeClass: "approved",
    };
  }
  if (s === "quoted") {
    return {
      badge: "Quoted",
      title: "Quote sent for payment",
      subtitle: "Pricing finalized and shared with user",
      icon: "ri-file-list-3-line",
      iconClass: "quoted",
      badgeClass: "quoted",
    };
  }
  if (s === "design_approved") {
    return {
      badge: "Design Approved",
      title: "Design approved",
      subtitle: "Final artwork confirmed by user",
      icon: "ri-checkbox-circle-line",
      iconClass: "approved",
      badgeClass: "approved",
    };
  }
  if (s === "completed") {
    return {
      badge: "Completed",
      title: "Converted to Sales Order",
      subtitle: "Payment received, ready for production",
      icon: "ri-shopping-cart-line",
      iconClass: "completed",
      badgeClass: "completed",
    };
  }
  return {
    badge: "In Progress",
    title: "Artwork revisions in progress",
    subtitle: "Iterating on designs based on feedback",
    icon: "ri-edit-2-line",
    iconClass: "inprogress",
    badgeClass: "inprogress",
  };
};

const DESIGNER_SEARCH_PAGE_SIZE = 10;
const QUOTE_ASSIGN_SEARCH_PAGE_SIZE = 10;

const DesignerDropdownIndicator = (props: any) => (
  <components.DropdownIndicator {...props}>
    <i className="ri-arrow-down-s-line fs-5" style={{ color: "#6c757d" }} />
  </components.DropdownIndicator>
);

const formatDateTime = (value: string) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
};

const designerAsyncSelectStyles = {
  dropdownIndicator: (provided: any) => ({
    ...provided,
    display: "flex",
    opacity: 1,
    visibility: "visible",
  }),
  indicatorSeparator: (provided: any) => ({
    ...provided,
    display: "none",
  }),
};

/** In-flow menu (no body portal): aligns under the control; card uses overflow:visible so list is not clipped. */
function getDesignerSelectStyles(
  controlWidth: number
): StylesConfig<SignageDesignerSelectOption, false, GroupBase<SignageDesignerSelectOption>> {
  return {
    ...designerAsyncSelectStyles,
    container: (base) => ({
      ...base,
      width: "100%",
    }),
    control: (base) => ({
      ...base,
      minHeight: 44,
    }),
    menu: (base) =>
      controlWidth > 0
        ? {
            ...base,
            width: controlWidth,
            minWidth: controlWidth,
            maxWidth: controlWidth,
            zIndex: 50,
          }
        : { ...base, zIndex: 50 },
    menuList: (base) => ({
      ...base,
      maxHeight: 280,
      paddingTop: 4,
      paddingBottom: 4,
    }),
    option: (base) => ({
      ...base,
      whiteSpace: "normal",
      wordWrap: "break-word",
      overflowWrap: "anywhere",
      lineHeight: 1.35,
      paddingTop: 8,
      paddingBottom: 8,
    }),
    singleValue: (base) => ({
      ...base,
      maxWidth: "100%",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }),
  };
}

type AddAgentPartyOption = { value: string; label: string };

/** Menu matches control width; long labels wrap instead of stretching the dropdown. */
function getAddAgentPartySelectStyles(
  controlWidth: number
): StylesConfig<AddAgentPartyOption, false, GroupBase<AddAgentPartyOption>> {
  return {
    ...designerAsyncSelectStyles,
    container: (base) => ({
      ...base,
      width: "100%",
    }),
    control: (base) => ({
      ...base,
      minHeight: 44,
    }),
    indicatorsContainer: (base) => ({
      ...base,
      gap: 0,
    }),
    clearIndicator: (base) => ({
      ...base,
      display: "flex",
    }),
    menu: (base) =>
      controlWidth > 0
        ? {
            ...base,
            width: controlWidth,
            minWidth: controlWidth,
            maxWidth: controlWidth,
          }
        : base,
    /** Render via portal so lists are not clipped by cards with overflow:hidden */
    menuPortal: (base) => ({
      ...base,
      zIndex: 10050,
    }),
    menuList: (base) => ({
      ...base,
      maxHeight: 180,
      paddingTop: 4,
      paddingBottom: 4,
    }),
    option: (base) => ({
      ...base,
      whiteSpace: "normal",
      wordWrap: "break-word",
      overflowWrap: "anywhere",
      lineHeight: 1.35,
    }),
    singleValue: (base) => ({
      ...base,
      maxWidth: "100%",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }),
  };
}

function useObserveDivColumnWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setWidth(Math.round(el.getBoundingClientRect().width));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, width };
}

/** Quick-pick emojis for "Message to Agent" (Unicode, works without image assets). */
const MESSAGE_TO_AGENT_EMOJIS = [
  "🙂",
  "👍",
  "❤️",
  "😃",
  "🎉",
  "👏",
  "🙏",
  "✅",
  "💯",
  "🔥",
  "💪",
  "😁",
  "🤝",
  "⭐",
  "✨",
  "💼",
  "📋",
  "📝",
  "🏠",
  "🔑",
  "💰",
  "📞",
  "✉️",
  "⏰",
];

const MESSAGE_EMOJI_POP_MIN_W = 300;

function getMessageEmojiPopPosition(wrapEl: HTMLDivElement) {
  const r = wrapEl.getBoundingClientRect();
  const left = Math.min(
    window.innerWidth - MESSAGE_EMOJI_POP_MIN_W - 8,
    Math.max(8, r.right - MESSAGE_EMOJI_POP_MIN_W)
  );
  return { top: r.bottom + 8, left };
}

const SignageAddEditprojectandquote: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams<{ projectId: string }>();
  const [showAddAgentForm, setShowAddAgentForm] = useState(false);
  const [showAddBrokerageAdminForm, setShowAddBrokerageAdminForm] = useState(false);
  const [confirmAgentChangeOpen, setConfirmAgentChangeOpen] = useState(false);
  const [editAgentSelectionMode, setEditAgentSelectionMode] = useState(false);
  const [messageToAgent, setMessageToAgent] = useState("");
  const [sendMessageToAgent, setSendMessageToAgent] = useState(true);
  const [showMessageToAgentEmojiPicker, setShowMessageToAgentEmojiPicker] = useState(false);
  const [messageEmojiPopPos, setMessageEmojiPopPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const artworkFileInputRef = useRef<HTMLInputElement>(null);
  const messageToAgentTextareaRef = useRef<HTMLTextAreaElement>(null);
  const messageToAgentEmojiWrapRef = useRef<HTMLDivElement>(null);
  const messageToAgentEmojiPopRef = useRef<HTMLDivElement>(null);
  const [projectArtworkFiles, setProjectArtworkFiles] = useState<File[]>([]);
  const [artworkDragActive, setArtworkDragActive] = useState(false);
  const [isSavingProject, setIsSavingProject] = useState(false);
  const [editProjectData, setEditProjectData] = useState<SignageProjectDetail | null>(null);
  const editMessages: any[] = [];

  const mergeArtworkFiles = (incoming: FileList | File[]) => {
    const add: File[] = [];
    for (const f of Array.from(incoming)) {
      if (!isAllowedArtworkFile(f)) {
        showError(`Unsupported format: ${f.name}. Use PNG, JPG, PDF, or AI.`);
        continue;
      }
      add.push(f);
    }
    if (add.length) setProjectArtworkFiles((prev) => [...prev, ...add]);
  };

  const removeArtworkAt = (index: number) => {
    setProjectArtworkFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const insertMessageToAgentEmoji = (emoji: string) => {
    setMessageToAgent((prev) => prev + emoji);
    setShowMessageToAgentEmojiPicker(false);
    setMessageEmojiPopPos(null);
    setTimeout(() => {
      const ta = messageToAgentTextareaRef.current;
      if (ta) {
        ta.focus();
        const len = ta.value.length;
        ta.setSelectionRange(len, len);
      }
    }, 0);
  };

  useLayoutEffect(() => {
    if (!showMessageToAgentEmojiPicker) return;
    const updatePosition = () => {
      const wrap = messageToAgentEmojiWrapRef.current;
      if (wrap) setMessageEmojiPopPos(getMessageEmojiPopPosition(wrap));
    };
    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [showMessageToAgentEmojiPicker]);

  useEffect(() => {
    if (!showMessageToAgentEmojiPicker) return;
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as Node;
      const w = messageToAgentEmojiWrapRef.current;
      const p = messageToAgentEmojiPopRef.current;
      if (w?.contains(t) || p?.contains(t)) return;
      setShowMessageToAgentEmojiPicker(false);
      setMessageEmojiPopPos(null);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [showMessageToAgentEmojiPicker]);

  const token = getAuthCookie("sessionToken") || "";
  const userId = getAuthCookie("userId") || "";
  const organizationId = getAuthCookie("organizationId");
  const effectiveSignageOrgId = resolveSignageSessionOrganizationId() || organizationId || userId || "";
  const membershipId = getAuthCookie("membershipId") || "";
  const globalUserId = getAuthCookie("globalUserId") || "";
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries: ["places"],
  });

  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);
  const placesDummyNodeRef = useRef<HTMLDivElement | null>(null);
  const addressAutocompleteWrapRef = useRef<HTMLDivElement | null>(null);
  const addressPredictDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [placePredictions, setPlacePredictions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const [addressSuggestionsOpen, setAddressSuggestionsOpen] = useState(false);
  const [addressHighlightIndex, setAddressHighlightIndex] = useState(-1);

  const isEdit =
    Boolean(projectId) ||
    (location.state as { mode?: string } | null)?.mode === "edit";

  const isSignageTeammember = location.pathname.includes("/signageteammember");
  const projectsListPath = isSignageTeammember ? "/signageteammember/projects" : "/signage/projects";

  // Org-scoped firm id from session cookie / resolveSignageSessionOrganizationId
  const signageFirmID =
    String(organizationId || "").trim() ||
    effectiveSignageOrgId;

  const [selectedDesignerOption, setSelectedDesignerOption] =
    useState<SignageDesignerSelectOption | null>(null);
  const [selectedQuoteAssigneeOption, setSelectedQuoteAssigneeOption] =
    useState<SignageDesignerSelectOption | null>(null);
  const [isSavingQuoteAssignee, setIsSavingQuoteAssignee] = useState(false);

  useEffect(() => {
    if (!isEdit) setSelectedDesignerOption(null);
  }, [signageFirmID, isEdit]);

  const loadDesignerOptions: LoadOptions<
    SignageDesignerSelectOption,
    any,
    { page: number }
  > = useCallback(
    async (inputValue, _loaded, additional) => {
      const page = additional?.page ?? 1;
      const q = (inputValue || "").trim();
      if (!token || !signageFirmID) {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }
      try {
        const { options, hasMore } = await fetchUsersSearchSignageTeamMembers(
          token,
          organizationId || "",
          { page, limit: DESIGNER_SEARCH_PAGE_SIZE, search: q }
        );
        return {
          options,
          hasMore,
          additional: { page: page + 1 },
        };
      } catch (e) {
        showError(e instanceof Error ? e.message : "Failed to load designers");
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [token, signageFirmID, organizationId]
  );

  const [projectTitle, setProjectTitle] = useState("");
  const [projectNotes, setProjectNotes] = useState("");
  const [projectSearch, setProjectSearch] = useState<{
    licenseType: string;
    item: any;
  } | null>(null);
  // console.log("projectSearch", projectSearch);
  const [projectContact, setProjectContact] = useState<ProjectSearchContact>(emptyProjectContact);
  const [projectOffice, setProjectOffice] = useState<{ _id: string; officeName: string } | null>(
    null
  );
  const [projectTeamMemberPick, setProjectTeamMemberPick] = useState<ProjectPersonPick | null>(
    null
  );
  const [projectAgentPick, setProjectAgentPick] = useState<ProjectPersonPick | null>(null);
  const [teamMemberFormRefreshKey, setTeamMemberFormRefreshKey] = useState(0);
  const projectSearchRef = useRef<SearchBrokerageAgentTeamHandle | null>(null);
  const pendingSearchPickRef = useRef<Record<string, unknown> | null>(null);

  const projectBrokerageFirmId = useMemo(() => {
    const item = projectSearch?.item;
    if (!item) return undefined;
    const licenseType = String(
      projectSearch?.licenseType || item?.licenseType || ""
    ).toUpperCase();
    if (licenseType === "BROKERAGE_FIRM") {
      const id = String(item.id || item._id || item.value || "").trim();
      return id || undefined;
    }
    const affiliated = String(
      item.brokerageFirmID || item.brokerage_firm_id || item.brokerageFirmId || ""
    ).trim();
    return affiliated || undefined;
  }, [projectSearch]);

  useEffect(() => {
    if (!isEdit || !projectId || !token) return;
    let cancelled = false;
    (async () => {
      try {
        const detail = await fetchProjectById(token, projectId);
        if (cancelled) return;
        setEditProjectData(detail);
        setProjectTitle(detail.title || "");
        setProjectNotes(detail.notes || "");
        if (detail.designer_id || detail.designer_name) {
          setSelectedDesignerOption({
            value: detail.designer_membership_id || detail.designer_id || "",
            membershipId: detail.designer_membership_id || detail.designer_id || "",
            globalUserId: detail.designer_global_user_id || "",
            label: detail.designer_name || "Assigned Designer",
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
          });
        } else {
          setSelectedDesignerOption(null);
        }
        if ((detail as any)?.assigned_to_id || (detail as any)?.assigned_to_name) {
          setSelectedQuoteAssigneeOption({
            value:
              detail.assigned_to_membership_id ||
              String((detail as any)?.assigned_to_id || ""),
            membershipId:
              detail.assigned_to_membership_id ||
              String((detail as any)?.assigned_to_id || ""),
            globalUserId: detail.assigned_to_global_user_id || "",
            label: String((detail as any)?.assigned_to_name || "").trim() || "Assigned Member",
            firstName: "",
            lastName: "",
            email: String((detail as any)?.payment_email || "").trim(),
            phone: "",
          });
        } else {
          setSelectedQuoteAssigneeOption(null);
        }
      } catch (e) {
        if (!cancelled) {
          showError(e instanceof Error ? e.message : "Failed to load project");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, projectId, token]);

  useEffect(() => {
    setConfirmAgentChangeOpen(false);
    setEditAgentSelectionMode(false);
  }, [projectId]);

  // "Add New Agent" form data + API-driven dropdowns.
  const [offices, setOffices] = useState<Office[]>([]);
  const [agentFormLoading, setAgentFormLoading] = useState(false);
  const [agentCreating, setAgentCreating] = useState(false);
  const [addressLine1Touched, setAddressLine1Touched] = useState(false);

  const [agentForm, setAgentForm] = useState<{
    firstName: string;
    lastName: string;
    phone1: string;
    phone2: string;
    email: string;
    password: string;

    brokerageFirmAffiliation: string;
    officeId: string;

    agentID: string;
    licenseNumber: string;
    licenseExpiryDate: string;

    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    zipcode: string;
    country: string;
    cityCode: string;
    stateCode: string;
    countryCode: string;
    latitude: number;
    longitude: number;
  }>({
    firstName: "",
    lastName: "",
    phone1: "",
    phone2: "",
    email: "",
    password: "",

    brokerageFirmAffiliation: "",
    officeId: "",

    agentID: "",
    licenseNumber: "",
    licenseExpiryDate: "",

    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zipcode: "",
    country: "",
    cityCode: "",
    stateCode: "",
    countryCode: "",
    latitude: 0,
    longitude: 0,
  });

  const addressLine1Error = useMemo(() => {
    if (!addressLine1Touched) return "";
    try {
      addressLine1Validation.validateSync(agentForm.addressLine1);
      return "";
    } catch (e: unknown) {
      return e instanceof Error ? e.message : "";
    }
  }, [addressLine1Touched, agentForm.addressLine1]);

  const ensurePlacesServices = () => {
    if (!window.google?.maps?.places) return false;
    if (!autocompleteServiceRef.current) {
      autocompleteServiceRef.current = new google.maps.places.AutocompleteService();
    }
    if (!placesServiceRef.current) {
      if (!placesDummyNodeRef.current) {
        const el = document.createElement("div");
        el.style.display = "none";
        el.setAttribute("aria-hidden", "true");
        document.body.appendChild(el);
        placesDummyNodeRef.current = el;
      }
      placesServiceRef.current = new google.maps.places.PlacesService(
        placesDummyNodeRef.current
      );
    }
    return true;
  };

  const applyPlaceResult = (place: google.maps.places.PlaceResult) => {
    if (!place?.address_components) {
      showError("Please select a valid address from the suggestions.");
      return;
    }
    if (!place.geometry) return;

    const addressComponents = place.address_components;
    const getComponent = (type: string) => {
      const component = addressComponents?.find((c) => c.types.includes(type));
      return component
        ? { long_name: component.long_name, short_name: component.short_name }
        : { long_name: "", short_name: "" };
    };

    const streetNumber = getComponent("street_number").long_name;
    const route = getComponent("route").long_name;
    const cityObj = getComponent("locality") || getComponent("administrative_area_level_2");
    const stateObj = getComponent("administrative_area_level_1");
    const postalCode = getComponent("postal_code").long_name;
    const countryObj = getComponent("country");
    const street = `${streetNumber} ${route}`.trim();
    const latitude = place.geometry?.location?.lat();
    const longitude = place.geometry?.location?.lng();

    setAgentForm((p) => ({
      ...p,
      addressLine1: street || place.formatted_address || p.addressLine1,
      city: cityObj.long_name || p.city,
      cityCode: cityObj.short_name || p.cityCode,
      state: stateObj.long_name || p.state,
      stateCode: stateObj.short_name || p.stateCode,
      zipcode: postalCode || p.zipcode,
      country: countryObj.long_name || p.country,
      countryCode: countryObj.short_name || p.countryCode,
      latitude: typeof latitude === "number" ? latitude : p.latitude,
      longitude: typeof longitude === "number" ? longitude : p.longitude,
    }));

    if (!postalCode && typeof latitude === "number" && typeof longitude === "number") {
      fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
      )
        .then((res) => res.json())
        .then((data) => {
          const postalComp = data.results?.[0]?.address_components?.find((c: { types: string[] }) =>
            c.types.includes("postal_code")
          );
          if (postalComp) {
            setAgentForm((p) => ({ ...p, zipcode: postalComp.long_name }));
          }
        })
        .catch(() => {});
    }
  };

  const fetchAddressPredictions = (input: string) => {
    if (!ensurePlacesServices() || !autocompleteServiceRef.current) return;
    const q = input.trim();
    if (q.length < 2) {
      setPlacePredictions([]);
      return;
    }
    autocompleteServiceRef.current.getPlacePredictions(
      { input: q },
      (results, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && results?.length) {
          setPlacePredictions(results);
          setAddressHighlightIndex(0);
        } else {
          setPlacePredictions([]);
          setAddressHighlightIndex(-1);
        }
      }
    );
  };

  const selectPlacePrediction = (prediction: google.maps.places.AutocompletePrediction) => {
    if (!ensurePlacesServices() || !placesServiceRef.current) return;
    placesServiceRef.current.getDetails(
      {
        placeId: prediction.place_id,
        fields: ["address_component", "geometry", "formatted_address"],
      },
      (place, status) => {
        if (status !== google.maps.places.PlacesServiceStatus.OK || !place) {
          showError("Could not load address details. Try again.");
          return;
        }
        applyPlaceResult(place);
        setAddressSuggestionsOpen(false);
        setPlacePredictions([]);
        setAddressHighlightIndex(-1);
      }
    );
  };

  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      const el = addressAutocompleteWrapRef.current;
      if (!el || !addressSuggestionsOpen) return;
      if (e.target instanceof Node && !el.contains(e.target)) {
        setAddressSuggestionsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [addressSuggestionsOpen]);

  useEffect(() => {
    return () => {
      if (addressPredictDebounceRef.current) {
        clearTimeout(addressPredictDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      const n = placesDummyNodeRef.current;
      if (n?.parentNode) {
        n.parentNode.removeChild(n);
      }
      placesDummyNodeRef.current = null;
      placesServiceRef.current = null;
    };
  }, []);

  const [addAgentBrokerageOption, setAddAgentBrokerageOption] = useState<AddAgentPartyOption | null>(null);

  const loadAddAgentBrokerageOptions: LoadOptions<AddAgentPartyOption, GroupBase<AddAgentPartyOption>, { page: number }> =
    useCallback(
      async (inputValue, _loadedOptions, additional) => {
        const page = additional?.page ?? 1;
        const ownerId = String(signageFirmID || "").trim();
        if (!token || !ownerId) {
          return { options: [], hasMore: false, additional: { page } };
        }
        try {
          const params = new URLSearchParams({
            licenseType: "BROKERAGE_FIRM",
            query: String(inputValue || "").trim(),
            page: String(page),
            limit: "10",
          });
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/organizations/${encodeURIComponent(ownerId)}/memberships/select?${params.toString()}`,
            {
              headers: {
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );
          const raw = await response.json().catch(() => ({}));
          if (!response.ok) {
            throw new Error(raw?.error || raw?.message || "Failed to fetch brokerage firms");
          }
          const results = Array.isArray(raw?.data)
            ? raw.data
            : Array.isArray(raw?.results)
              ? raw.results
              : Array.isArray(raw)
                ? raw
                : [];
          const options: AddAgentPartyOption[] = results
            .map((item: any) => ({
              value: String(
                item?.organization_id || item?.organizationId || item?.id || item?._id || ""
              ).trim(),
              label: String(
                item?.company_name ||
                  item?.firmDetails?.companyName ||
                  item?.label ||
                  item?.text ||
                  ""
              ).trim(),
            }))
            .filter((option: AddAgentPartyOption) => option.value && option.label);
          const currentPage = Number(raw?.page ?? page);
          const totalPages = Number(raw?.totalPages ?? currentPage);
          const hasMore = Boolean(raw?.pagination?.more) || currentPage < totalPages;
          return { options, hasMore, additional: { page: page + 1 } };
        } catch (err) {
          showError(err instanceof Error ? err.message : "Failed to load brokerage firms");
          return { options: [], hasMore: false, additional: { page } };
        }
      },
      [signageFirmID, token]
    );

  const selectedBrokerage = useMemo(() => {
    if (!addAgentBrokerageOption?.value) return null;
    return {
      id: addAgentBrokerageOption.value,
      firmDetails: { companyName: addAgentBrokerageOption.label },
    };
  }, [addAgentBrokerageOption]);

  const selectedOffice = useMemo(() => {
    if (!agentForm.officeId) return null;
    return (Array.isArray(offices) ? offices : []).find((o) => o._id === agentForm.officeId) || null;
  }, [offices, agentForm.officeId]);

  const brokeragePartySelectMeasure = useObserveDivColumnWidth();
  const officePartySelectMeasure = useObserveDivColumnWidth();
  const designerSelectMeasure = useObserveDivColumnWidth();

  const officePartyOptions = useMemo<AddAgentPartyOption[]>(
    () =>
      (Array.isArray(offices) ? offices : []).map((o) => ({
        value: o._id,
        label: o.officeName || "Office",
      })),
    [offices]
  );

  const officePartyValue = useMemo(
    () => officePartyOptions.find((o) => o.value === agentForm.officeId) ?? null,
    [officePartyOptions, agentForm.officeId]
  );

  const brokeragePartySelectStyles = useMemo(
    () => getAddAgentPartySelectStyles(brokeragePartySelectMeasure.width),
    [brokeragePartySelectMeasure.width]
  );

  const officePartySelectStyles = useMemo(
    () => getAddAgentPartySelectStyles(officePartySelectMeasure.width),
    [officePartySelectMeasure.width]
  );

  const designerPaginateSelectStyles = useMemo(
    () => getDesignerSelectStyles(designerSelectMeasure.width),
    [designerSelectMeasure.width]
  );

  const handleBrokerageFirmChange = async (firmId: string, option: AddAgentPartyOption | null = null) => {
    setAddAgentBrokerageOption(option);
    setAgentForm((prev) => ({
      ...prev,
      brokerageFirmAffiliation: firmId,
      officeId: "",
    }));

    if (!firmId) {
      setOffices([]);
      return;
    }

    if (!token) {
      showError("Session expired. Please login again.");
      return;
    }

    setAgentFormLoading(true);
    try {
      const data = await fetchOfficesByFirm(token, firmId);
      setOffices(data);
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to fetch offices");
      setOffices([]);
    } finally {
      setAgentFormLoading(false);
    }
  };

  const applyCreatedMemberToProjectSearch = useCallback(
    (created: BrokerageTeammemberCreatedResult) => {
      const fullName = `${created.firstName} ${created.lastName}`.trim();
      const brokerageName = created.brokerageFirmName || "";
      const officeList = Array.isArray(created.offices) ? created.offices : [];
      const firstOffice = officeList[0];
      const membershipId = String(created.membershipId || created.id || "").trim();
      const globalUserId = String(created.globalUserId || "").trim();
      const organizationId = String(
        created.organizationId || created.brokerageFirmId || ""
      ).trim();

      const searchItem: Record<string, unknown> = {
        value: membershipId,
        membershipId,
        membership_id: membershipId,
        globalUserId,
        global_user_id: globalUserId,
        organizationId,
        organization_id: organizationId,
        label: fullName,
        displayLabel: fullName,
        license_type: "BROKERAGE_TEAM_MEMBER",
        email: created.email,
        phone: created.phone,
        firstName: created.firstName,
        lastName: created.lastName,
        company_name: brokerageName,
        brokerage_name: brokerageName,
        brokerage_firm_id: created.brokerageFirmId,
        brokerage_firm_organization_id: created.brokerageFirmId,
        brokerage_firm_name: brokerageName,
        office_count: officeList.length,
        offices:
          officeList.length === 1 && firstOffice
            ? { _id: firstOffice._id, officeName: firstOffice.officeName }
            : officeList.map((o) => ({ _id: o._id, officeName: o.officeName })),
      };

      const nextContact: ProjectSearchContact = {
        customer_id: membershipId,
        fName: created.firstName,
        lName: created.lastName,
        email: created.email,
        phone: created.phone || "0000000000",
      };

      const nextTeamMember: ProjectPersonPick = {
        id: membershipId,
        membershipId,
        globalUserId,
        organizationId,
        label: fullName,
        email: created.email,
        phone: created.phone || "0000000000",
        firstName: created.firstName,
        lastName: created.lastName,
      };

      pendingSearchPickRef.current = searchItem;
      setProjectTeamMemberPick(nextTeamMember);
      setProjectAgentPick(null);
      setProjectSearch({ licenseType: "BROKERAGE_TEAM_MEMBER", item: searchItem });
      setProjectContact(nextContact);
      if (firstOffice?._id) {
        setProjectOffice({
          _id: firstOffice._id,
          officeName: firstOffice.officeName || "",
        });
      } else {
        setProjectOffice(null);
      }
    },
    []
  );

  const handleBrokerageTeammemberCreated = useCallback(
    (created?: BrokerageTeammemberCreatedResult) => {
      if (!created?.id) {
        setShowAddBrokerageAdminForm(false);
        return;
      }
      applyCreatedMemberToProjectSearch(created);
      setShowAddBrokerageAdminForm(false);
      setTeamMemberFormRefreshKey((k) => k + 1);
    },
    [applyCreatedMemberToProjectSearch]
  );

  useEffect(() => {
    if (showAddBrokerageAdminForm || showAddAgentForm) return;
    const item = pendingSearchPickRef.current;
    if (!item) return;
    pendingSearchPickRef.current = null;
    const timer = window.setTimeout(() => {
      projectSearchRef.current?.restoreApplyPick(item);
    }, 80);
    return () => window.clearTimeout(timer);
  }, [showAddBrokerageAdminForm, showAddAgentForm, teamMemberFormRefreshKey]);

  const handleCreateAgent = async () => {
    if (agentCreating) return;

    setAddressLine1Touched(true);

    if (
      !agentForm.firstName.trim() ||
      !agentForm.lastName.trim() ||
      !agentForm.email.trim()
    ) {
      showError("Please fill all required fields to create the agent.");
      return;
    }

    if (!token) {
      showError("Session expired. Please login again.");
      return;
    }

    const brokerageFirmID = String(
      selectedBrokerage?.id || agentForm.brokerageFirmAffiliation || ""
    ).trim();
    const isBrokerageAgent = Boolean(brokerageFirmID);
    // Brokerage selected → that org id; otherwise session cookie organizationId.
    const targetOrganizationId = isBrokerageAgent
      ? brokerageFirmID
      : String(organizationId || "").trim();

    if (!targetOrganizationId) {
      showError("Organization ID is missing.");
      return;
    }

    setAgentCreating(true);
    try {
      const DEFAULT_TEN_ZEROS = "0000000000";
      const withDefault = (v?: string, def = DEFAULT_TEN_ZEROS) =>
        !v || v.trim() === "" || v === def ? def : v;
      const toCoordinate = (value: unknown) => {
        const coordinate = Number(value ?? 0);
        return Number.isFinite(coordinate) ? coordinate : 0;
      };

      const addressCountry = agentForm.country?.trim() || "United States";
      const addressCountryCode = agentForm.countryCode?.trim() || "US";

      const payload: Record<string, unknown> = {
        email: agentForm.email,
        password: agentForm.password || "",
        firstName: agentForm.firstName,
        lastName: agentForm.lastName,
        phone1: withDefault(agentForm.phone1),
        phone2: agentForm.phone2 || "",
        jobTitle: "",
        status: "ACTIVE",
        userType: isBrokerageAgent ? "brokerage_agent" : "agent",
        licenseType: isBrokerageAgent ? "BROKERAGE_AGENT" : "AGENT",
        professional_details: {
          agentID: agentForm.agentID,
          licenseNumber: agentForm.licenseNumber,
          licenseExpiryDate: agentForm.licenseExpiryDate,
        },
        address: {
          addressLine1: agentForm.addressLine1,
          addressLine2: agentForm.addressLine2 || "",
          city: agentForm.city,
          state: agentForm.state,
          country: addressCountry,
          zip: agentForm.zipcode,
          phone: withDefault(agentForm.phone1),
          latitude: toCoordinate(agentForm.latitude),
          longitude: toCoordinate(agentForm.longitude),
          cityCode: agentForm.cityCode,
          stateCode: agentForm.stateCode,
          countryCode: addressCountryCode,
        },
      };

      if (isBrokerageAgent) {
        payload.brokerageFirmID = brokerageFirmID;
        if (agentForm.officeId) {
          payload.officeIDs = [agentForm.officeId];
        }
      }

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/organizations/${targetOrganizationId}/memberships/agents`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );
      const result: any = await res.json();
      if (!res.ok || result?.error) {
        throw new Error(result?.error || result?.message || "Failed to create agent.");
      }

      const membership = result?.membership ?? result?.data?.membership ?? result;
      const newMembershipId = String(
        membership?._id ?? membership?.id ?? membership?.membership_id ?? ""
      ).trim();
      if (!newMembershipId) {
        throw new Error("Agent created but no membership id was returned.");
      }

      const newGlobalUserId = String(
        membership?.global_user_id ?? membership?.globalUserId ?? ""
      ).trim();
      const membershipOrgId = String(
        membership?.organization_id ??
          membership?.organizationId ??
          targetOrganizationId
      ).trim();

      const fullName = `${agentForm.firstName} ${agentForm.lastName}`.trim();
      const brokerageName = String(
        selectedBrokerage?.firmDetails?.companyName || addAgentBrokerageOption?.label || ""
      ).trim();
      const officeId = String(selectedOffice?._id || agentForm.officeId || "").trim();
      const officeName = String(
        selectedOffice?.officeName || officePartyValue?.label || ""
      ).trim();
      const licenseType = isBrokerageAgent ? "BROKERAGE_AGENT" : "AGENT";
      const nextContact: ProjectSearchContact = {
        customer_id: newMembershipId,
        fName: agentForm.firstName,
        lName: agentForm.lastName,
        email: agentForm.email,
        phone: withDefault(agentForm.phone1),
      };

      const nextAgent: ProjectPersonPick = {
        id: newMembershipId,
        membershipId: newMembershipId,
        globalUserId: newGlobalUserId,
        organizationId: membershipOrgId,
        label: fullName,
        email: agentForm.email,
        phone: withDefault(agentForm.phone1),
        firstName: agentForm.firstName,
        lastName: agentForm.lastName,
      };

      const searchItem: Record<string, unknown> = {
        value: newMembershipId,
        membershipId: newMembershipId,
        membership_id: newMembershipId,
        globalUserId: newGlobalUserId,
        global_user_id: newGlobalUserId,
        organizationId: membershipOrgId,
        organization_id: membershipOrgId,
        label: fullName,
        displayLabel: fullName,
        license_type: licenseType,
        email: agentForm.email,
        phone: withDefault(agentForm.phone1),
        firstName: agentForm.firstName,
        lastName: agentForm.lastName,
        ...(brokerageFirmID
          ? {
              company_name: brokerageName,
              brokerage_name: brokerageName,
              brokerage_firm_id: brokerageFirmID,
              brokerage_firm_name: brokerageName,
              brokerage_firm_organization_id: brokerageFirmID,
            }
          : {}),
        ...(officeId
          ? {
              officeName,
              office_name: officeName,
              offices: { _id: officeId, id: officeId, officeName },
            }
          : {}),
      };

      pendingSearchPickRef.current = searchItem;
      setProjectAgentPick(nextAgent);
      setProjectSearch({
        licenseType,
        item: searchItem,
      });
      setProjectContact(nextContact);
      if (officeId) {
        setProjectOffice({
          _id: officeId,
          officeName,
        });
      } else {
        setProjectOffice(null);
      }
      setProjectTeamMemberPick(null);

      showSuccess("Agent created successfully!");
      setShowAddAgentForm(false);
      setTeamMemberFormRefreshKey((k) => k + 1);
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to create agent.");
    } finally {
      setAgentCreating(false);
    }
  };

  const handlePrimaryAction = async () => {
    if (isSavingProject) return;

    if (isEdit) {
      if (!token) {
        showError("Session expired. Please login again.");
        return;
      }
      if (!projectId) {
        showError("Missing project id.");
        return;
      }
      if (!projectTitle.trim()) {
        showError("Please enter a project title.");
        return;
      }

      if (editAgentSelectionMode) {
        if (!projectSearch) {
          showError("Search and select a brokerage, office, agent, or team member.");
          return;
        }

        const searchValidation = projectSearchRef.current?.validate?.({
          officeId: projectOffice?._id,
          teamMemberId: projectTeamMemberPick?.id,
        });
        if (searchValidation && !searchValidation.valid) {
          showError(
            searchValidation.errors.teamMember ||
              searchValidation.errors.office ||
              searchValidation.errors.search ||
              "Complete office and team member selection."
          );
          return;
        }

        const { licenseType, item } = projectSearch;
        const contact = projectContact;

        const {
          teamMemberId,
          teamMemberName,
          agentId,
          agentName,
          primaryPersonId,
          customer_id,
          customerGlobalUserId,
        } = resolveProjectPersonPicks({
          licenseType,
          item,
          contact,
          projectTeamMemberPick,
          projectAgentPick,
        });

        let brokerage_firm_id = "";
        let brokerage_firm_name = "";
        if (licenseType === "BROKERAGE_FIRM") {
          brokerage_firm_id = String(item?.value ?? "");
          brokerage_firm_name = String(item?.company_name || item?.label || "");
        } else {
          brokerage_firm_id = String(
            item?.brokerage_firm_id ?? item?.brokerageFirmId ?? item?.brokerage_id ?? ""
          );
          brokerage_firm_name = String(
            item?.brokerage_firm_name ?? item?.brokerageFirmName ?? item?.brokerage_name ?? ""
          );
          if (!brokerage_firm_name && licenseType === "OFFICE" && item?.company_name) {
            brokerage_firm_name = String(item.company_name);
          }
        }

        let officePayload = projectOffice;
        if (!officePayload?._id && licenseType === "OFFICE" && item) {
          const nestedOffice = Array.isArray(item?.offices)
            ? item.offices[0]
            : item?.offices;
          const oid = String(
            nestedOffice?._id ?? nestedOffice?.id ?? item?.office_id ?? item?.value ?? ""
          );
          const oname = String(
            nestedOffice?.officeName ?? item?.office_name ?? item?.officeName ?? item?.label ?? ""
          );
          if (oid) officePayload = { _id: oid, officeName: oname };
        }

        const phoneForResolve = (contact.phone || "").trim();

        if (requiresAgentOrTeamMemberId(licenseType) && !primaryPersonId.trim()) {
          showError(
            "Select an agent or team member. For a brokerage, choose an office and team member when required."
          );
          return;
        }

        setIsSavingProject(true);
        try {
          const address = await resolveSignageProjectAddressForCreate(token, {
            searchItem: item,
            office: officePayload?._id ? officePayload : null,
            brokerageFirmId: brokerage_firm_id,
            projectTitle: projectTitle.trim(),
            customerPhone: phoneForResolve,
          });

          const customer_phone = pickCustomerPhone(
            phoneForResolve,
            String(address.phone || "").trim()
          );

          const office: OfficePayload = officePayload?._id
            ? { _id: officePayload._id, officeName: officePayload.officeName || "" }
            : { _id: "", officeName: "" };
          const party = buildProjectPartyPayload(
            {
              licenseType,
              agentId,
              agentName,
              teamMemberId,
              teamMemberName,
              customer_id,
              brokerage_firm_id,
              brokerage_firm_name,
              office,
              contact,
              customer_phone,
            },
            { forPatchPersonChange: true }
          );

          const customerMembershipId = resolveProjectCustomerMembershipId({
            licenseType,
            agentId,
            teamMemberId,
            customer_id,
            contactCustomerId: contact.customer_id,
          });

          const identity = buildProjectIdentityFields({
            organizationId: organizationId || "",
            membershipId,
            globalUserId,
            licenseType,
            searchItem: item,
            agentMembershipId: agentId,
            agentGlobalUserId: agentId
              ? String(projectAgentPick?.globalUserId || "").trim() ||
                (projectAgentPick ? "" : customerGlobalUserId)
              : "",
            agentOrgId: agentId
              ? String(projectAgentPick?.organizationId || "").trim()
              : "",
            teamMemberMembershipId: teamMemberId,
            teamMemberGlobalUserId: teamMemberId
              ? String(projectTeamMemberPick?.globalUserId || "").trim() ||
                (projectTeamMemberPick ? "" : customerGlobalUserId)
              : "",
            projectTeamMemberPick,
            projectAgentPick,
            customerMembershipId,
            customerGlobalUserId,
            brokerageFirmOrganizationId: resolveBrokerageFirmOrganizationId({
              licenseType,
              item,
              brokerage_firm_id,
            }),
            designer: selectedDesignerOption,
            assignedTo: selectedQuoteAssigneeOption,
          });

          // UpdateProjectRequest does not accept vendor_org_id (create-only).
          const { vendor_org_id: _omitVendorOrgId, ...updateIdentity } = identity;

          await updateProjectLinkedOrderType(token, projectId, {
            title: projectTitle.trim(),
            notes: projectNotes.trim(),
            designer_name: selectedDesignerOption?.label || "",
            send_email: sendMessageToAgent,
            send_message_to_client: sendMessageToAgent,
            ...party,
            ...updateIdentity,
            address,
          });
          showSuccess("Project updated successfully!");
          navigate(projectId ? `${projectsListPath}/view/${projectId}` : projectsListPath);
        } catch (error) {
          showError(error instanceof Error ? error.message : "Failed to update project.");
        } finally {
          setIsSavingProject(false);
        }
        return;
      }

      setIsSavingProject(true);
      try {
        const phoneRawEdit = getCustomerPhoneFromProjectDetail(editProjectData);

        const address = await resolveSignageProjectAddressForUpdate(token, {
          existingProject: editProjectData,
          searchItem: {},
          office: editProjectData?.office?._id
            ? {
                _id: String(editProjectData.office._id),
                officeName: String(editProjectData.office.officeName || ""),
              }
            : null,
          brokerageFirmId: String(editProjectData?.brokerage_firm_id || ""),
          projectTitle: projectTitle.trim(),
          customerPhone: phoneRawEdit,
        });

        const customer_phone = pickCustomerPhone(
          phoneRawEdit,
          String(address.phone || "").trim()
        );
        const fn = String(editProjectData?.first_name ?? "").trim();
        const ln = String(editProjectData?.last_name ?? "").trim();
        const customer_name =
          String(editProjectData?.customer_name ?? "").trim() ||
          [fn, ln].filter(Boolean).join(" ").trim() ||
          String(editProjectData?.agent_name ?? "").trim();
        const nameTokens = customer_name.split(/\s+/).filter(Boolean);
        const first_name = fn || nameTokens[0] || "";
        const last_name = ln || nameTokens.slice(1).join(" ") || "";

        const storedLt = String(editProjectData?.license_type ?? "").trim();
        const identity = buildProjectIdentityFields({
          organizationId: organizationId || "",
          membershipId,
          globalUserId,
          licenseType: storedLt,
          savedProject: editProjectData,
          agentMembershipId: String(
            editProjectData?.agent_membership_id ?? editProjectData?.agent_id ?? ""
          ).trim(),
          agentGlobalUserId: String(editProjectData?.agent_global_user_id ?? "").trim(),
          teamMemberMembershipId: String(
            editProjectData?.team_member_membership_id ??
              editProjectData?.team_member_id ??
              ""
          ).trim(),
          teamMemberGlobalUserId: String(
            editProjectData?.team_member_global_user_id ?? ""
          ).trim(),
          customerMembershipId: String(
            editProjectData?.customer_membership_id ??
              editProjectData?.customer_id ??
              editProjectData?.agent_id ??
              ""
          ).trim(),
          customerGlobalUserId: String(editProjectData?.customer_global_user_id ?? "").trim(),
          brokerageFirmOrganizationId: String(
            editProjectData?.brokerage_firm_organization_id ?? ""
          ).trim(),
          designer: selectedDesignerOption,
          assignedTo: selectedQuoteAssigneeOption,
        });
        // UpdateProjectRequest does not accept vendor_org_id (create-only).
        const { vendor_org_id: _omitVendorOrgId, ...updateIdentity } = identity;
        const updatePayload: any = {
          title: projectTitle.trim(),
          notes: projectNotes.trim(),
          designer_name: selectedDesignerOption?.label || "",
          send_email: sendMessageToAgent,
          send_message_to_client: sendMessageToAgent,
          address,
          customer_email: String(editProjectData?.customer_email ?? "").trim(),
          customer_name,
          customer_phone,
          first_name,
          last_name,
          ...updateIdentity,
          ...(storedLt ? { license_type: storedLt } : {}),
        };
        await updateProjectLinkedOrderType(token, projectId, updatePayload);
        showSuccess("Project updated successfully!");
        navigate(projectId ? `${projectsListPath}/view/${projectId}` : projectsListPath);
      } catch (error) {
        showError(error instanceof Error ? error.message : "Failed to update project.");
      } finally {
        setIsSavingProject(false);
      }
      return;
    }

    if (!projectTitle.trim()) {
      showError("Please enter a project title.");
      return;
    }
    if (!token) {
      showError("Session expired. Please login again.");
      return;
    }
    if (!signageFirmID) {
      showError("Missing signage firm context. Please refresh or try again.");
      return;
    }
    if (!projectSearch) {
      showError("Search and select a brokerage, office, agent, or team member.");
      return;
    }

    const searchValidation = projectSearchRef.current?.validate?.({
      officeId: projectOffice?._id,
      teamMemberId: projectTeamMemberPick?.id,
    });
    if (searchValidation && !searchValidation.valid) {
      showError(
        searchValidation.errors.teamMember ||
          searchValidation.errors.office ||
          searchValidation.errors.search ||
          "Complete office and team member selection."
      );
      return;
    }

    const { licenseType, item } = projectSearch;
    const contact = projectContact;

    const {
      teamMemberId,
      teamMemberName,
      agentId,
      agentName,
      primaryPersonId,
      customer_id,
      customerGlobalUserId,
    } = resolveProjectPersonPicks({
      licenseType,
      item,
      contact,
      projectTeamMemberPick,
      projectAgentPick,
    });

    let brokerage_firm_id = "";
    let brokerage_firm_name = "";
    if (licenseType === "BROKERAGE_FIRM") {
      brokerage_firm_id = String(item?.value ?? "");
      brokerage_firm_name = String(item?.company_name || item?.label || "");
    } else {
      brokerage_firm_id = String(
        item?.brokerage_firm_id ?? item?.brokerageFirmId ?? item?.brokerage_id ?? ""
      );
      brokerage_firm_name = String(
        item?.brokerage_firm_name ?? item?.brokerageFirmName ?? item?.brokerage_name ?? ""
      );
      if (!brokerage_firm_name && licenseType === "OFFICE" && item?.company_name) {
        brokerage_firm_name = String(item.company_name);
      }
    }

    let officePayload = projectOffice;
    if (!officePayload?._id && licenseType === "OFFICE" && item) {
      const nestedOffice = Array.isArray(item?.offices) ? item.offices[0] : item?.offices;
      const oid = String(
        nestedOffice?._id ?? nestedOffice?.id ?? item?.office_id ?? item?.value ?? ""
      );
      const oname = String(
        nestedOffice?.officeName ?? item?.office_name ?? item?.officeName ?? item?.label ?? ""
      );
      if (oid) officePayload = { _id: oid, officeName: oname };
    }

    const phoneForResolve = (contact.phone || "").trim();

    if (requiresAgentOrTeamMemberId(licenseType) && !primaryPersonId.trim()) {
      showError(
        "Select an agent or team member. For a brokerage, choose an office and team member when required."
      );
      return;
    }

    setIsSavingProject(true);

    try {
      const address = await resolveSignageProjectAddressForCreate(token, {
        searchItem: item,
        office: officePayload?._id ? officePayload : null,
        brokerageFirmId: brokerage_firm_id,
        projectTitle: projectTitle.trim(),
        customerPhone: phoneForResolve,
      });

      const customer_phone = pickCustomerPhone(
        phoneForResolve,
        String(address.phone || "").trim()
      );

      const office: OfficePayload = officePayload?._id
        ? { _id: officePayload._id, officeName: officePayload.officeName || "" }
        : { _id: "", officeName: "" };
      const party = buildProjectPartyPayload({
        licenseType,
        agentId,
        agentName,
        teamMemberId,
        teamMemberName,
        customer_id,
        brokerage_firm_id,
        brokerage_firm_name,
        office,
        contact,
        customer_phone,
      });

      const customerMembershipId = resolveProjectCustomerMembershipId({
        licenseType,
        agentId,
        teamMemberId,
        customer_id,
        contactCustomerId: contact.customer_id,
      });

      const identity = buildProjectIdentityFields({
        organizationId: organizationId || "",
        membershipId,
        globalUserId,
        licenseType,
        searchItem: item,
        agentMembershipId: agentId,
        agentGlobalUserId: agentId
          ? String(projectAgentPick?.globalUserId || "").trim() ||
            (projectAgentPick ? "" : customerGlobalUserId)
          : "",
        agentOrgId: agentId
          ? String(projectAgentPick?.organizationId || "").trim()
          : "",
        teamMemberMembershipId: teamMemberId,
        teamMemberGlobalUserId: teamMemberId
          ? String(projectTeamMemberPick?.globalUserId || "").trim() ||
            (projectTeamMemberPick ? "" : customerGlobalUserId)
          : "",
        projectTeamMemberPick,
        projectAgentPick,
        customerMembershipId,
        customerGlobalUserId,
        brokerageFirmOrganizationId: resolveBrokerageFirmOrganizationId({
          licenseType,
          item,
          brokerage_firm_id,
        }),
        designer: selectedDesignerOption,
        // CreateProjectRequest has no assigned_to_* — omit on create.
      });

      const created = await createSignageProject(token, {
        title: projectTitle.trim(),
        notes: projectNotes.trim(),
        designer_name: selectedDesignerOption?.label || "",
        mute_messages_to_client: false,
        send_message_to_client: true,
        address,
        ...party,
        ...identity,
        vendor_org_id: signageFirmID,
      });

      const newProjectId = parseCreatedProjectId(created);
      const followUpErrors: string[] = [];

      if (newProjectId) {
        if (projectArtworkFiles.length > 0) {
          try {
            await uploadSignageProjectFiles(
              token,
              signageFirmID,
              newProjectId,
              projectArtworkFiles
            );
          } catch (e) {
            followUpErrors.push(
              e instanceof Error ? e.message : "Failed to upload artwork files."
            );
          }
        }
        if (messageToAgent.trim()) {
          try {
            await postProjectMessage(token, newProjectId, messageToAgent.trim());
          } catch (e) {
            followUpErrors.push(
              e instanceof Error ? e.message : "Failed to send message to agent."
            );
          }
        }
      } else if (projectArtworkFiles.length > 0 || messageToAgent.trim()) {
        followUpErrors.push(
          "Project was created but the response did not include a project id; artwork and message were not saved."
        );
      }

      // Trigger end-user email only when requested by checkbox.
      if (sendMessageToAgent && newProjectId) {
        try {
          const recipientEmail = (contact.email || String(item?.email || "")).trim();
          if (recipientEmail) {
            const lu = String(licenseType || "").toUpperCase();
            const recipientLicense =
              lu === "BROKERAGE_AGENT"
                ? "BROKERAGE_AGENT"
                : lu === "BROKERAGE_TEAM_MEMBER"
                  ? "BROKERAGE_TEAM_MEMBER"
                  : "AGENT";
            const customerName =
              [contact.fName, contact.lName].filter(Boolean).join(" ").trim() ||
              agentName ||
              recipientEmail.split("@")[0];
            const baseUrl = (process.env.REACT_APP_BASE_URL || window.location.origin).replace(
              /\/+$/,
              ""
            );
            let reviewUrl = `${baseUrl}/agent/design-review-portal?project_id=${encodeURIComponent(
              newProjectId
            )}&access_token=`;
            let publicProjectId = newProjectId;
            try {
              const publicRes = await fetchProjectPublicAccess(token, newProjectId);
              const p = (publicRes as any)?.data ?? (publicRes as any);
              publicProjectId =
                String(p?.project_id || p?.projectId || newProjectId || "").trim() || newProjectId;
              const rawPublicUrl = String(p?.public_url || "").trim();
              const tokenFromPublicUrl = rawPublicUrl.includes("?")
                ? new URLSearchParams(rawPublicUrl.split("?")[1]).get("token") || ""
                : "";
              const accessToken = String(
                p?.access_token || p?.accessToken || tokenFromPublicUrl || ""
              ).trim();
              reviewUrl = `${baseUrl}/agent/design-review-portal?Public_url=${encodeURIComponent(
                rawPublicUrl
              )}&project_id=${encodeURIComponent(publicProjectId)}&access_token=${encodeURIComponent(
                accessToken
              )}`;
            } catch (publicErr) {
              console.warn("Failed to resolve project public access URL:", publicErr);
            }

            const teamMemberCc = String(projectTeamMemberPick?.email || "").trim();
            const licenseTypes: string[] = [recipientLicense];
            const recipients: string[] = [recipientEmail];
            if (
              teamMemberCc &&
              teamMemberCc.toLowerCase() !== recipientEmail.toLowerCase()
            ) {
              licenseTypes.push("BROKERAGE_TEAM_MEMBER");
              recipients.push(teamMemberCc);
            }

            const firmEmail = resolveBrokerageFirmEmail({
              ...item,
              license_type: licenseType,
              brokerage_firm_name: brokerage_firm_name,
            });
            if (firmEmail) {
              const lower = firmEmail.toLowerCase();
              if (!recipients.some((r) => r.toLowerCase() === lower)) {
                licenseTypes.push("BROKERAGE_FIRM");
                recipients.push(firmEmail);
              }
            }

            // await sendTriggerEmails(
            //   "New_project_create",
            //   licenseTypes,
            //   {
            //     customerName,
            //     propertyAddress: projectTitle.trim() || "your property",
            //     signType: "design",
            //     projectId: publicProjectId,
            //     reviewUrl,
            //     publicUrl: reviewUrl,
            //     brokerageFirmName: brokerage_firm_name || "",
            //   },
            //   recipients,
            //   process.env.REACT_APP_FROM_EMAIL2 || "",
            //   token || ""
            // );
          }
        } catch (emailErr) {
          console.warn("Failed to send New_project_create email:", emailErr);
        }
      }

      showSuccess("Project created successfully!");
      if (followUpErrors.length > 0) {
        showError(followUpErrors.join(" "));
      }
      navigate(newProjectId ? `${projectsListPath}/view/${newProjectId}` : projectsListPath);
    } catch (error) {
      console.error("Failed to create project:", error);
      showError(error instanceof Error ? error.message : "Failed to create project.");
    } finally {
      setIsSavingProject(false);
    }
  };

  const editNameParts = useMemo(() => {
    const full = (editProjectData?.agent_name || editProjectData?.team_member_name || "").trim();
    if (!full) return { first: "—", last: "—" };
    const parts = full.split(/\s+/);
    return { first: parts[0] || "—", last: parts.slice(1).join(" ") || "—" };
  }, [editProjectData]);

  const editDisplayPhone = useMemo(
    () => getCustomerPhoneFromProjectDetail(editProjectData),
    [editProjectData]
  );

  const editOfficeAddr = useMemo(() => {
    const projectAddr = editProjectData?.address as Record<string, unknown> | undefined;
    if (projectAddr && typeof projectAddr === "object") {
      return {
        addressLine1: String(projectAddr.addressLine1 ?? ""),
        addressLine2: String(projectAddr.addressLine2 ?? ""),
        city: String(projectAddr.city ?? ""),
        state: String(projectAddr.state ?? ""),
        zip: String(projectAddr.zip ?? ""),
      };
    }

    const officeRaw = editProjectData?.office?.officeAddress as
      | Record<string, unknown>
      | Record<string, unknown>[]
      | undefined;
    const officeAddr =
      Array.isArray(officeRaw) && officeRaw.length > 0
        ? officeRaw[0]
        : !Array.isArray(officeRaw) && officeRaw && typeof officeRaw === "object"
          ? officeRaw
          : null;

    if (!officeAddr) {
      return {
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        zip: "",
      };
    }

    return {
      addressLine1: String(officeAddr.addressLine1 ?? ""),
      addressLine2: String(officeAddr.addressLine2 ?? ""),
      city: String(officeAddr.city ?? ""),
      state: String(officeAddr.state ?? ""),
      zip: String(officeAddr.zip ?? ""),
    };
  }, [editProjectData?.address, editProjectData?.office?.officeAddress]);
  const editWorkflowUi = useMemo(
    () => getEditWorkflowStatusUi(editProjectData?.status),
    [editProjectData?.status]
  );

  const canEditLinkedAgent = useMemo(() => {
    const s = String(editProjectData?.status ?? "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_");
    return s === "in_progress";
  }, [editProjectData?.status]);

  const cancelEditAgentChangeFlow = () => {
    setEditAgentSelectionMode(false);
    setShowAddAgentForm(false);
    setProjectSearch(null);
    setProjectContact(emptyProjectContact);
    setProjectOffice(null);
    setProjectTeamMemberPick(null);
  };

  return (
  <div className="pq-create-page">
    <div className="pq-create-header">
      <div>
        <h1 className="pq-create-title">
          {isEdit ? "Edit Project" : "Create a Project"}
        </h1>
        <p className="pq-create-subtitle">
          {isEdit
            ? "Update project details and requirements"
            : "Start a new design conversation with an agent"}
        </p>
      </div>

      <button
        className="pq-top-cancel-btn"
        type="button"
        onClick={() => navigate(projectsListPath)}
      >
        Cancel
      </button>
    </div>

    <div className="pq-create-layout">
      <div className="pq-create-left">
        <div className="pq-create-card">
          <div className="pq-create-card-body">
            
            <div className="pq-form-group">
              {/* <label className="pq-form-label">
                Search Brokerage, Office, Agent, or Admin Name{" "}
                <span className="text-danger">*</span>
              </label> */}

              {isEdit && !editAgentSelectionMode ? (
                <div className="pq-create-card-body-Details">
                <div className="pq-agent-details-card">
                  <div className="pq-agent-card-head">
                    <h3>User Details</h3>
                    {canEditLinkedAgent ? (
                      <button
                        type="button"
                        className="pq-agent-edit-btn"
                        onClick={() => setConfirmAgentChangeOpen(true)}
                      >
                        <i className="ri-edit-line" />
                        Edit
                      </button>
                    ) : null}
                  </div>

                  <div className="pq-agent-grid-details">
                    <div>
                      <div className="pq-k">First Name</div>
                      <div className="pq-v">{editNameParts.first}</div>
                    </div>

                    <div>
                      <div className="pq-k">Last Name</div>
                      <div className="pq-v">{editNameParts.last}</div>
                    </div>

                    <div>
                      <div className="pq-k">Email</div>
                      <div className="pq-v">{editProjectData?.customer_email || "—"}</div>
                    </div>

                    <div>
                      <div className="pq-k">Phone</div>
                      <div className="pq-v">{editDisplayPhone || "—"}</div>
                    </div>

                    <div>
                      <div className="pq-k">Brokerage</div>
                      <div className="pq-v">{editProjectData?.brokerage_firm_name || "—"}</div>
                    </div>

                    <div>
                      <div className="pq-k">Office</div>
                      <div className="pq-v">{editProjectData?.office?.officeName || "—"}</div>
                    </div>

                    <div>
                      <div className="pq-k">Address Line 1</div>
                      <div className="pq-v">{editOfficeAddr?.addressLine1 || "—"}</div>
                    </div>

                    <div>
                      <div className="pq-k">Address Line 2</div>
                      <div className="pq-v">{editOfficeAddr?.addressLine2 || "—"}</div>
                    </div>

                    <div>
                      <div className="pq-k">City</div>
                      <div className="pq-v">{editOfficeAddr?.city || "—"}</div>
                    </div>

                    <div>
                      <div className="pq-k">State</div>
                      <div className="pq-v">{editOfficeAddr?.state || "—"}</div>
                    </div>

                    <div>
                      <div className="pq-k">Zip Code</div>
                      <div className="pq-v">{editOfficeAddr?.zip || "—"}</div>
                    </div>
                  </div>
                </div>
                </div>
              ) : (
                <>
                  {isEdit && editAgentSelectionMode ? (
                    <div className="pq-agent-change-banner" role="status">
                      <i className="ri-alert-line pq-agent-change-banner-icon" aria-hidden />
                      <span className="pq-agent-change-banner-text">
                        Changing the agent will affect all future communication and quote data.
                      </span>
                      <button
                        type="button"
                        className="pq-agent-change-banner-cancel"
                        onClick={cancelEditAgentChangeFlow}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : null}
                  {!showAddAgentForm && !showAddBrokerageAdminForm ? (
                    <div className="pq-search-holder">
                      <div style={{ flex: 1, minWidth: 280 }}>
                        <SearchBrokerageAgentTeam
                          ref={projectSearchRef}
                          contactNameReadOnly={false}
                          key={
                            isEdit && editAgentSelectionMode
                              ? `edit-agent-repick-${teamMemberFormRefreshKey}`
                              : `create-project-${teamMemberFormRefreshKey}`
                          }
                          organizationId={organizationId || ""}
                          token={token}
                          initialType={projectSearch?.licenseType || undefined}
                          initialItem={projectSearch?.item}
                          initialLabel={
                            String(projectSearch?.item?.displayLabel || projectSearch?.item?.label || "").trim() ||
                            undefined
                          }
                          initialContact={
                            projectContact.fName ||
                            projectContact.lName ||
                            projectContact.email ||
                            projectContact.phone
                              ? projectContact
                              : undefined
                          }
                          title={isEdit && editAgentSelectionMode ? "Change linked agent" : "Create Project"}
                          // subtitle={
                          //   isEdit && editAgentSelectionMode
                          //     ? "Search by name, brokerage, or office..."
                          //     : "Search by Brokerage, Office, Agent, or Admin Name"
                          // }
                          onContinue={(payload: {
                            type: string;
                            item: any;
                            contact: { customer_id?: string; fName: string; lName: string; email: string; phone: string };
                          }) => {
                            if (!payload?.item) return;
                            setProjectSearch({ licenseType: payload.type || "", item: payload.item });
                            setProjectAgentPick(null);
                            if (payload?.contact) {
                              setProjectContact({
                                customer_id:
                                  String(
                                    payload.contact.customer_id ||
                                      payload.item?.membershipId ||
                                      payload.item?.customer_id ||
                                      payload.item?.value ||
                                      ""
                                  ).trim(),
                                fName: payload.contact.fName || "",
                                lName: payload.contact.lName || "",
                                email: payload.contact.email || "",
                                phone: payload.contact.phone || "0000000000",
                              });
                            }
                          }}
                          onContactChange={(c: { customer_id?: string; fName: string; lName: string; email: string; phone: string }) => {
                            setProjectContact({
                              customer_id: String(c?.customer_id || projectContact.customer_id || "").trim(),
                              fName: c?.fName || "",
                              lName: c?.lName || "",
                              email: c?.email || "",
                              phone: c?.phone || "0000000000",
                            });
                          }}
                          onTeamMemberChange={(member: {
                            id: string;
                            membershipId?: string;
                            globalUserId?: string;
                            organizationId?: string;
                            label: string;
                            firstName: string;
                            lastName: string;
                            email: string;
                            phone: string;
                          } | null) => {
                            if (!member?.id) {
                              setProjectTeamMemberPick(null);
                              return;
                            }
                            setProjectTeamMemberPick({
                              id: String(member.membershipId ?? member.id),
                              membershipId: String(member.membershipId ?? member.id),
                              globalUserId: String(member.globalUserId ?? ""),
                              organizationId: String(member.organizationId ?? ""),
                              label: member.label || "",
                              email: member.email || "",
                              phone: member.phone || "0000000000",
                              firstName: member.firstName || "",
                              lastName: member.lastName || "",
                            });

                            const memberId = String(member.membershipId ?? member.id);
                            const c: ProjectSearchContact = {
                              customer_id: memberId,
                              fName: member.firstName || "",
                              lName: member.lastName || "",
                              email: member.email || "",
                              phone: member.phone || "0000000000",
                            };
                            setProjectContact(c);
                          }}
                          onAgentChange={(agent: {
                            id: string;
                            membershipId?: string;
                            globalUserId?: string;
                            organizationId?: string;
                            label: string;
                            firstName: string;
                            lastName: string;
                            email: string;
                            phone: string;
                          } | null) => {
                            if (!agent?.id) {
                              setProjectAgentPick(null);
                              return;
                            }

                            const nextAgent: ProjectPersonPick = {
                              id: String(agent.membershipId ?? agent.id),
                              membershipId: String(agent.membershipId ?? agent.id),
                              globalUserId: String(agent.globalUserId ?? ""),
                              organizationId: String(agent.organizationId ?? ""),
                              label: agent.label || "",
                              email: agent.email || "",
                              phone: agent.phone || "0000000000",
                              firstName: agent.firstName || "",
                              lastName: agent.lastName || "",
                            };
                            setProjectAgentPick(nextAgent);
                            setProjectContact({
                              customer_id: nextAgent.id,
                              fName: nextAgent.firstName,
                              lName: nextAgent.lastName,
                              email: nextAgent.email,
                              phone: nextAgent.phone,
                            });
                          }}
                          onOfficeChange={(office: {
                            id?: string;
                            _id?: string;
                            label?: string;
                          }) => {
                            const oid = String(office?.id ?? office?._id ?? "");
                            const oname = String(office?.label ?? "");
                            if (oid) setProjectOffice({ _id: oid, officeName: oname });
                            setProjectAgentPick(null);
                            setProjectTeamMemberPick(null);
                          }}
                          onReset={() => {
                            setProjectSearch(null);
                            setProjectContact(emptyProjectContact);
                            setProjectOffice(null);
                            setProjectTeamMemberPick(null);
                            setProjectAgentPick(null);
                          }}
                        />
                      </div>
                      <div className="pq-add-agent-btn-row">
                        <button
                          type="button"
                          className="pq-add-agent-btn"
                          onClick={() => {
                            setShowAddBrokerageAdminForm(false);
                            setShowAddAgentForm(true);
                          }}
                        >
                          <i className="ri-add-line" />
                          Add New Agent
                        </button>
                        <button
                          type="button"
                          className="pq-add-agent-btn pq-add-agent-btn--manager"
                          onClick={() => {
                            setShowAddAgentForm(false);
                            setShowAddBrokerageAdminForm(true);
                          }}
                        >
                          <i className="ri-user-add-line" />
                          Add Brokerage Admin / Manager
                        </button>
                      </div>
                    </div>
                  ) : showAddBrokerageAdminForm ? (
                    <div className="pq-add-agent-form-wrap">
                      <div className="pq-add-agent-header py-3">
                        <div>
                          <h3 className="pq-add-agent-title">Add New Team Member</h3>
                          <p className="pq-add-agent-subtitle">
                          Create a new team member profile for your real estate team
                          </p>
                        </div>
                        <button
                          type="button"
                          className="pq-add-agent-close"
                          onClick={() => setShowAddBrokerageAdminForm(false)}
                        >
                          <i className="ri-close-line" />
                        </button>
                      </div>
                      <BrokerageTeammemberCreationComponent
                        variant="inline"
                        firmId={projectBrokerageFirmId}
                        onClose={() => setShowAddBrokerageAdminForm(false)}
                        onSuccess={handleBrokerageTeammemberCreated}
                      />
                    </div>
                  ) : (
                    <div className="pq-add-agent-form-wrap">
                      <div className="pq-add-agent-header py-3">
                        <div>
                          <h3 className="pq-add-agent-title">Add New Agent</h3>
                          <p className="pq-add-agent-subtitle">
                            Create a new agent profile for your real estate team
                          </p>
                        </div>

                        <button
                          type="button"
                          className="pq-add-agent-close"
                          onClick={() => setShowAddAgentForm(false)}
                        >
                          <i className="ri-close-line" />
                        </button>
                      </div>

                      <div className="pq-add-agent-section">
                        <div className="pq-add-agent-section-head">
                          <i className="ri-user-line orange" /> Personal Information
                        </div>

                        <div className="pq-add-agent-grid-2">
                          <div>
                            <label className="pq-form-label">
                              First Name <span className="text-danger">*</span>
                            </label>
                            <input
                              className="pq-text-input"
                              placeholder="Enter first name"
                              value={agentForm.firstName}
                              onChange={(e) =>
                                setAgentForm((p) => ({ ...p, firstName: e.target.value }))
                              }
                            />
                          </div>

                          <div>
                            <label className="pq-form-label">
                              Last Name <span className="text-danger">*</span>
                            </label>
                            <input
                              className="pq-text-input"
                              placeholder="Enter last name"
                              value={agentForm.lastName}
                              onChange={(e) =>
                                setAgentForm((p) => ({ ...p, lastName: e.target.value }))
                              }
                            />
                          </div>

                          <div>
                            <label className="pq-form-label">
                              Phone 
                              {/* <span className="text-danger">*</span> */}
                            </label>
                            <input
                              className="pq-text-input"
                              placeholder="0000000000"
                              value={agentForm.phone1}
                              onChange={(e) =>
                                setAgentForm((p) => ({ ...p, phone1: e.target.value }))
                              }
                            />
                          </div>

                          <div>
                            <label className="pq-form-label">Secondary Phone</label>
                            <input
                              className="pq-text-input"
                              placeholder="Optional"
                              value={agentForm.phone2}
                              onChange={(e) =>
                                setAgentForm((p) => ({ ...p, phone2: e.target.value }))
                              }
                            />
                          </div>
                        </div>
                      </div>

                      <div className="pq-add-agent-section">
                        <div className="pq-add-agent-section-head">
                          <i className="ri-shield-user-line green" /> Account Access
                        </div>

                        <div className="pq-add-agent-grid-1">
                          <div>
                            <label className="pq-form-label">
                              Email <span className="text-danger">*</span>
                            </label>
                            <input
                              id="agent-email"
                              name="email"
                              type="email"
                              className="pq-text-input"
                              placeholder="Email address"
                              autoComplete="off"
                              spellCheck={false}
                              value={agentForm.email}
                              onChange={(e) =>
                                setAgentForm((p) => ({ ...p, email: e.target.value }))
                              }
                            />
                          </div>
                        </div>
                      </div>

                      <div className="pq-add-agent-section">
                        <div className="pq-add-agent-section-head">
                          <i className="ri-briefcase-4-line purple" /> Professional Details
                        </div>

                        <div className="pq-add-agent-grid-2">
                          <div className="pq-add-agent-select-cell">
                            <label className="pq-form-label">
                              Brokerage Firm Affiliation 
                              {/* <span className="text-danger">*</span> */}
                            </label>
                            <div
                              ref={brokeragePartySelectMeasure.ref}
                              className="pq-add-agent-react-select"
                            >
                              <AsyncPaginate<AddAgentPartyOption, GroupBase<AddAgentPartyOption>, { page: number }, false>
                                classNamePrefix="pq-add-agent-rs"
                                placeholder="Search brokerage firms..."
                                value={addAgentBrokerageOption}
                                loadOptions={loadAddAgentBrokerageOptions}
                                onChange={(opt) => {
                                  void handleBrokerageFirmChange(opt?.value ?? "", opt ?? null);
                                }}
                                additional={{ page: 1 }}
                                defaultOptions
                                debounceTimeout={300}
                                isClearable
                                menuPosition="absolute"
                                styles={brokeragePartySelectStyles}
                                components={{ DropdownIndicator: DesignerDropdownIndicator }}
                                noOptionsMessage={() => "No firms found"}
                              />
                            </div>
                          </div>

                          <div className="pq-add-agent-select-cell">
                            <label className="pq-form-label">
                              Office 
                              {/* <span className="text-danger">*</span> */}
                            </label>
                            <div
                              ref={officePartySelectMeasure.ref}
                              className="pq-add-agent-react-select"
                            >
                              <Select<AddAgentPartyOption, false>
                                classNamePrefix="pq-add-agent-rs"
                                options={officePartyOptions}
                                value={officePartyValue}
                                onChange={(opt) =>
                                  setAgentForm((p) => ({ ...p, officeId: opt?.value ?? "" }))
                                }
                                placeholder="Select an office…"
                                isSearchable
                                isLoading={agentFormLoading}
                                isDisabled={!agentForm.brokerageFirmAffiliation}
                                isClearable
                                menuPosition="absolute"
                                styles={officePartySelectStyles}
                                components={{ DropdownIndicator: DesignerDropdownIndicator }}
                                noOptionsMessage={() =>
                                  agentForm.brokerageFirmAffiliation
                                    ? "No offices found"
                                    : "Choose a brokerage first"
                                }
                              />
                            </div>
                          </div>

                          <div>
                            <label className="pq-form-label">Agent ID/Code</label>
                            <input
                              className="pq-text-input"
                              placeholder="Enter Agent ID/Code"
                              value={agentForm.agentID}
                              onChange={(e) =>
                                setAgentForm((p) => ({ ...p, agentID: e.target.value }))
                              }
                            />
                          </div>

                          <div>
                            <label className="pq-form-label">License Number</label>
                            <input
                              className="pq-text-input"
                              placeholder="Enter license number"
                              value={agentForm.licenseNumber}
                              onChange={(e) =>
                                setAgentForm((p) => ({
                                  ...p,
                                  licenseNumber: e.target.value,
                                }))
                              }
                            />
                          </div>

                          <div>
                            <label className="pq-form-label">License Expiry Date</label>
                            <input
                              className="pq-text-input"
                              placeholder="dd-mm-yyyy"
                              value={agentForm.licenseExpiryDate}
                              onChange={(e) =>
                                setAgentForm((p) => ({
                                  ...p,
                                  licenseExpiryDate: e.target.value,
                                }))
                              }
                            />
                          </div>
                        </div>
                      </div>

                      <div className="pq-add-agent-section">
                        <div className="pq-add-agent-section-head">
                          <i className="ri-map-pin-line pink" /> Address Details
                        </div>

                        <div className="pq-add-agent-grid-1">
                          <div>
                            <label className="pq-form-label">
                              Address line 1 
                              {/* <span className="text-danger">*</span> */}
                            </label>
                            <AddressAutocomplete
                              id="addressLine1"
                              name="addressLine1"
                              className={`pq-text-input ${addressLine1Touched && addressLine1Error ? "is-invalid" : ""}`}
                              dropdownClassName="pq-address-autocomplete-menu"
                              placeholder="Enter address line 1"
                              value={agentForm.addressLine1}
                              onChange={(v) =>
                                setAgentForm((p) =>
                                  v.trim()
                                    ? { ...p, addressLine1: v }
                                    : {
                                        ...p,
                                        addressLine1: v,
                                        city: "",
                                        cityCode: "",
                                        state: "",
                                        stateCode: "",
                                        zipcode: "",
                                        country: "",
                                        countryCode: "",
                                        latitude: 0,
                                        longitude: 0,
                                      }
                                )
                              }
                              onSelect={(picked) => {
                                setAgentForm((p) => ({
                                  ...p,
                                  addressLine1: picked.addressLine1,
                                  city: picked.city,
                                  cityCode: picked.cityCode,
                                  state: picked.state,
                                  stateCode: picked.stateCode,
                                  zipcode: picked.zipcode,
                                  country: picked.country,
                                  countryCode: picked.countryCode,
                                  latitude: picked.latitude,
                                  longitude: picked.longitude,
                                }));
                              }}
                              // error={addressLine1Touched && addressLine1Error ? addressLine1Error : undefined}
                            />
                          </div>

                          <div>
                            <label className="pq-form-label">Address Line 2</label>
                            <input
                              className="pq-text-input"
                              placeholder="Apartment, suite, etc. (Optional)"
                              value={agentForm.addressLine2}
                              onChange={(e) =>
                                setAgentForm((p) => ({ ...p, addressLine2: e.target.value }))
                              }
                            />
                          </div>

                          <div className="pq-add-agent-grid-3" style={{ padding: 0 }}>
                            <div>
                              <label className="pq-form-label">
                                City
                                 {/* <span className="text-danger">*</span> */}
                              </label>
                              <input
                                className="pq-text-input"
                                placeholder="City"
                                value={agentForm.city}
                                onChange={(e) =>
                                  setAgentForm((p) => ({ ...p, city: e.target.value }))
                                }
                                disabled
                              />
                            </div>

                            <div>
                              <label className="pq-form-label">
                                State 
                                 {/* <span className="text-danger">*</span> */}
                              </label>
                              <input
                                className="pq-text-input"
                                placeholder="State"
                                value={agentForm.state}
                                onChange={(e) =>
                                  setAgentForm((p) => ({ ...p, state: e.target.value }))
                                }
                                disabled
                              />
                            </div>

                            <div>
                              <label className="pq-form-label">
                                Zip Code 
                                {/* <span className="text-danger">*</span> */}
                              </label>
                              <input
                                className="pq-text-input"
                                placeholder="Zip"
                                value={agentForm.zipcode}
                                onChange={(e) =>
                                  setAgentForm((p) => ({ ...p, zipcode: e.target.value }))
                                }
                                disabled
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="pq-bottom-actions mb-3">
                        <button
                          type="button"
                          className="pq-footer-cancel-btn"
                          onClick={() => setShowAddAgentForm(false)}
                        >
                          Back
                        </button>

                        <button
                          type="button"
                          className="pq-footer-primary-btn me-3"
                          onClick={handleCreateAgent}
                          disabled={agentCreating || agentFormLoading}
                        >
                          Create Agent
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="pq-form-group">
              <label className="pq-form-label">
                Project Title <span className="text-danger">*</span>
              </label>
              <input
                className="pq-text-input"
                placeholder="e.g. 123 Main St Yard Sign Rider"
                value={projectTitle}
                onChange={(e) => setProjectTitle(e.target.value)}
              />
            </div>
            <div className="pq-form-group">
              <label className="pq-form-label">
                Design Request Notes <span className="pq-light-text">(Internal)</span>
              </label>
              <textarea
                className="pq-textarea-input"
                placeholder={
                  isEdit
                    ? ""
                    : "Describe the signage or artwork requested by the agent...\n\nExample: 24x18 rider with phone number and brokerage logo"
                }
                value={projectNotes}
                onChange={(e) => setProjectNotes(e.target.value)}
              />
              <p className="pq-helper-text">
                Visible only to internal team members, not shared with agents.
              </p>
            </div>

            <div className="pq-form-group">
              <label className="pq-form-label">Assign Designer</label>
              <div ref={designerSelectMeasure.ref} className="pq-designer-group">
                {/* <AsyncSelect
                  cacheOptions
                  defaultOptions
                  value={selectedDesigner}
                  loadOptions={loadDesignerOptions}
                  onChange={setSelectedDesigner as any}
                  placeholder="Search designers..."
                  styles={designerAsyncSelectStyles}
                  isClearable
                /> */}
                 <AsyncPaginate
                      key={signageFirmID || "firm"}
                      classNamePrefix="pq-designer-rs"
                      placeholder="Search designers..."
                      value={
                        selectedDesignerOption
                          ? {
                              value: selectedDesignerOption.value,
                              label: selectedDesignerOption.label,
                              firstName: selectedDesignerOption.firstName,
                              lastName: selectedDesignerOption.lastName,
                              email: selectedDesignerOption.email,
                              phone: selectedDesignerOption.phone,
                            }
                          : null
                      }
                      loadOptions={loadDesignerOptions}
                      onChange={(opt) => {
                        if (opt) {
                          setSelectedDesignerOption(opt as SignageDesignerSelectOption);
                        } else {
                          setSelectedDesignerOption(null);
                        }
                      }}
                      additional={{ page: 1 }}
                      defaultOptions
                      debounceTimeout={300}
                      isClearable
                      menuPlacement="bottom"
                      components={{ DropdownIndicator: DesignerDropdownIndicator }}
                      formatOptionLabel={renderSignagePersonOptionLabel}
                      styles={designerPaginateSelectStyles}
                    />
              </div>
            </div>

            <div className="pq-form-group">
              {isEdit ? (
                <div className="pq-conversation-disabled">
                  <div className="pq-conversation-scroll">
                    {(Array.isArray(editMessages) ? editMessages : []).length === 0 ? (
                      <p className="text-muted mb-0">No conversation yet.</p>
                    ) : (
                      (Array.isArray(editMessages) ? editMessages : []).map((msg: any, idx: number) => (
                        <div key={msg?._id || idx} style={{ marginBottom: 14 }}>
                          <div className="pq-k">
                            {msg?.sender_name || "—"} • {msg?.created_at ? formatDateTime(msg.created_at) : "—"}
                          </div>
                          <div className="pq-v">{msg?.message || "—"}</div>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="pq-conversation-footer-note">
                    Go to the project view page to add new messages
                  </div>
                </div>
              ) : (
                <>
                <div className="pq-message-card">
                  {/* <div className="pq-message-label-row">
                    <label className="pq-form-label" style={{ marginBottom: 0 }}>
                      Message to Agent
                    </label>

                    <div className="pq-message-emoji-wrap" ref={messageToAgentEmojiWrapRef}>
                      <button
                        type="button"
                        className="pq-emoji-inline-btn"
                        aria-expanded={showMessageToAgentEmojiPicker}
                        aria-haspopup="listbox"
                        aria-label="Add emoji"
                        onClick={() => {
                          if (showMessageToAgentEmojiPicker) {
                            setShowMessageToAgentEmojiPicker(false);
                            setMessageEmojiPopPos(null);
                            return;
                          }
                          const wrap = messageToAgentEmojiWrapRef.current;
                          if (wrap) setMessageEmojiPopPos(getMessageEmojiPopPosition(wrap));
                          setShowMessageToAgentEmojiPicker(true);
                        }}
                      >
                        <i className="ri-emotion-line" aria-hidden />
                        Add Emoji
                      </button>
                    </div>
                  </div> */}

                  {/* <textarea
                    ref={messageToAgentTextareaRef}
                    className="pq-message-textarea"
                    placeholder="Type a message to the agent..."
                    value={messageToAgent}
                    onChange={(e) => setMessageToAgent(e.target.value)}
                  /> */}

                  {/* <label className="pq-message-check">
                    <input
                      type="checkbox"
                      checked={sendMessageToAgent}
                      onChange={(e) => setSendMessageToAgent(e.target.checked)}
                    />
                    <span>Send this message to agent</span>
                  </label> */}
                </div>
                {/* {showMessageToAgentEmojiPicker && messageEmojiPopPos
                  ? createPortal(
                      <div
                        ref={messageToAgentEmojiPopRef}
                        className="pq-message-emoji-pop"
                        style={{
                          top: messageEmojiPopPos.top,
                          left: messageEmojiPopPos.left,
                        }}
                        role="listbox"
                      >
                        <div className="pq-message-emoji-grid">
                          {MESSAGE_TO_AGENT_EMOJIS.map((em) => (
                            <button
                              key={em}
                              type="button"
                              className="pq-message-emoji-item"
                              role="option"
                              onClick={() => insertMessageToAgentEmoji(em)}
                            >
                              {em}
                            </button>
                          ))}
                        </div>
                      </div>,
                      document.body
                    )
                  : null} */}
                </>
              )}
            </div>

            <div className="pq-bottom-actions">
              <button
                className="pq-footer-cancel-btn"
                type="button"
                onClick={() => navigate(projectsListPath)}
              >
                Cancel
              </button>

              <button
                className="pq-footer-primary-btn"
                type="button"
                onClick={handlePrimaryAction}
                disabled={isSavingProject}
              >
                {isEdit ? "Update Project" : "Create Project"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="pq-create-right">
        <div className="pq-workflow-card">
          <div className="pq-workflow-card-head">Project Workflow</div>

          <div className="pq-workflow-card-body">
            {!isEdit ? (
              <div className="pq-workflow-list-wrap">
                {createWorkflowItems.map((item, index) => (
                  <div className="pq-workflow-row" key={item.label}>
                    <div className="pq-workflow-left">
                      <div className={`pq-workflow-icon ${item.tone}`}>
                        <i className={item.iconClass} />
                      </div>
                      {index !== createWorkflowItems.length - 1 && (
                        <div className="pq-workflow-line" />
                      )}
                    </div>

                    <div className="pq-workflow-content">
                      <div className="pq-workflow-title">{item.label}</div>
                      <div className="pq-workflow-detail">{item.detail}</div>
                      {item.assignedTo && (
                        <div className="pq-workflow-assigned">{item.assignedTo}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="pq-edit-status-card">
                <div className="pq-edit-status-top">
                  <div className={`pq-edit-status-icon ${editWorkflowUi.iconClass}`}>
                    <i className={editWorkflowUi.icon} />
                  </div>
                  <span className={`pq-edit-status-badge ${editWorkflowUi.badgeClass}`}>
                    {editWorkflowUi.badge}
                  </span>
                </div>

                <div className="pq-edit-status-title">{editWorkflowUi.title}</div>
                <div className="pq-edit-status-text">{editWorkflowUi.subtitle}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>

    {confirmAgentChangeOpen ? (
      <div
        className="pq-agent-change-overlay"
        role="presentation"
        onClick={() => setConfirmAgentChangeOpen(false)}
      >
        <div
          className="pq-agent-change-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pq-agent-change-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="pq-agent-change-modal-head">
            <div className="pq-agent-change-modal-icon" aria-hidden>
              <i className="ri-alert-fill" />
            </div>
            <h2 id="pq-agent-change-title" className="pq-agent-change-modal-title">
              Confirm User Change
            </h2>
          </div>
          <p className="pq-agent-change-modal-body">
            Changing user will affect all future communication and quote data.{" "}
            <strong>Are you sure?</strong>
          </p>
          <div className="pq-agent-change-modal-actions">
            <button
              type="button"
              className="pq-agent-change-modal-btn pq-agent-change-modal-btn--outline"
              onClick={() => setConfirmAgentChangeOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="pq-agent-change-modal-btn pq-agent-change-modal-btn--confirm"
              onClick={() => {
                setConfirmAgentChangeOpen(false);
                setEditAgentSelectionMode(true);
              }}
            >
              Yes, Change User
            </button>
          </div>
        </div>
      </div>
    ) : null}
  </div>
);
};

export default SignageAddEditprojectandquote;
